/**
 * SidebarNav — de enige menukolom van de AppShell (menu-ronde 2, 25 sep 2026).
 * Desktop: vaste zijbalk van 240px; het actieve hoofditem klapt zijn sectie open
 * (accordeon — alleen de actieve sectie). Focusschermen: ingeklapt tot een strook
 * met », hover schuift het menu erover. Mobiel: drawer via de hamburger.
 * Zoeken zit in de kopbalk (icoon + ⌘K), versie + beheer in het profielmenu.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useUserRole } from '../contexts/UserRoleContext';
import { useVisibilityFilters, isItemVisible } from '../contexts/VisibilityFiltersContext';
import { getSidebarItems, isSidebarItemActive, MENU_GROEPEN } from '../config/navigation';
import { useSectieMenu } from './SecondaryNav';
import { TESTBALK_TOP, VENSTER_ONDER_TESTBALK } from '../utils/testbalk';

const SIDEBAR_WIDTH = 240;
const FOCUS_RAIL_WIDTH = 44;
// Per focusscherm onthouden of de gebruiker het menu daar heeft vastgezet.
const FOCUS_MENU_KEY = 'ff:nav:focus-menu-vast:v1';

const PAARS = '#635983';
const LEI = '#556071';
const FONT_BODY = '"Inter", system-ui, sans-serif';

function leesVastgezet() {
  try { return JSON.parse(window.localStorage.getItem(FOCUS_MENU_KEY) || '{}') || {}; } catch { return {}; }
}

// Maatvoering hoofditem: rij-inset 8px + rand 3px + padding 12px + icoonkolom 30px.
// Subitems lijnen hun tekst uit op het label van het hoofditem (geen extra inspringing).
const ICOON_KOLOM = 30;
const LABEL_X = 3 + 12 + ICOON_KOLOM; // vanaf de linkerkant van de rij

/** Actief/hover-vlak: identiek voor hoofd- en subitems (één accent in het menu). */
const rijStijl = (actief) => ({
  borderRadius: '0.5rem',
  bgcolor: actief ? 'rgba(99,89,131,0.08)' : 'transparent',
  borderLeft: actief ? `3px solid ${PAARS}` : '3px solid transparent',
  '&:hover': { bgcolor: actief ? 'rgba(99,89,131,0.12)' : 'rgba(0,0,0,0.03)' },
});

/** Scherm onder een uitgeklapt hoofditem: volle rijbreedte, tekst onder het label. */
function SubItem({ to, label, active, planned, meer, onClick }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        textDecoration: 'none',
        mx: 1, my: '1px',
        pl: `${LABEL_X - 3}px`, pr: 1.5, py: 0.55,
        ...rijStijl(active),
      }}
    >
      <Typography noWrap sx={{
        fontFamily: FONT_BODY,
        fontSize: '0.8rem',
        fontWeight: active ? 600 : 500,
        color: active ? PAARS : planned ? '#c4c8ca' : LEI,
      }}>
        {label}
      </Typography>
      {meer && <ChevronRightIcon aria-hidden sx={{ fontSize: 15, color: active ? PAARS : '#9aa1a6', ml: '-2px' }} />}
    </Box>
  );
}

/** Uitgeklapte sectie (accordeon): max. 4 kernschermen + "Alle …" naar de hub. */
function SectieAccordeon({ sectionKey, onNavigate }) {
  const menu = useSectieMenu(sectionKey);
  if (!menu || (menu.items.length === 0 && !menu.alle)) return null;
  return (
    <Box role="group" sx={{ pb: 0.75 }}>
      {menu.items.map((it) => (
        <SubItem key={it.path} to={it.path} label={it.label} active={it.active} planned={it.planned} onClick={onNavigate} />
      ))}
      {menu.alle && (
        <SubItem to={menu.alle.path} label={menu.alle.label} active={menu.alle.active} meer onClick={onNavigate} />
      )}
    </Box>
  );
}

/** Klein stil kopje boven een menugroep (zoals een "Data studio"-scheiding). */
function GroepKop({ label }) {
  return (
    <Typography
      component="li"
      role="presentation"
      sx={{
        listStyle: 'none',
        fontFamily: FONT_BODY,
        fontSize: '0.62rem',
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#9aa1a6',
        pl: `${8 + LABEL_X - ICOON_KOLOM}px`,
        pt: 2.25, pb: 0.5,
      }}
    >
      {label}
    </Typography>
  );
}

function SidebarContent({ onClose, onCollapse, onPin }) {
  const theme = useTheme();
  const location = useLocation();
  const { permissions } = useUserRole();
  const { instance } = useMsal();
  const userEmail = instance.getActiveAccount()?.username || "";
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showBeta, showPlanned } = useVisibilityFilters();

  const rawItems = getSidebarItems(permissions, userEmail, { showBeta, showPlanned });
  // Categorie/hub verbergen op basis van beta/planned-filter (los van rol-toegang)
  const vis = (it) => isItemVisible(it, { showBeta, showPlanned });
  const items = {
    ...rawItems,
    primary: (rawItems.primary || []).filter(vis),
    bottom: (rawItems.bottom || []).filter(vis),
  };

  const isActive = (item) => isSidebarItemActive(item, location.pathname);

  const handleClick = () => {
    if (isMobile && onClose) onClose();
  };

  const renderItem = (item) => {
    const active = isActive(item);
    // Actieve sectie klapt open; het hoofditem blijft dan gewoon (niet-actief) gestyled —
    // het enige accent is het scherm waar je echt op staat.
    const open = active && !!item.submenu;
    const vlak = active && !open;
    const IconComponent = item.Icon;
    return (
      <React.Fragment key={item.label}>
        <ListItem disablePadding sx={{ px: 1, py: '1px' }}>
          <ListItemButton
            component={RouterLink}
            to={item.path}
            onClick={handleClick}
            aria-expanded={item.submenu ? open : undefined}
            sx={{
              py: 0.6,
              px: 1.5,
              minHeight: 34,
              boxShadow: 'none',
              ...rijStijl(vlak),
            }}
          >
            <ListItemIcon sx={{ minWidth: ICOON_KOLOM }}>
              <IconComponent
                fontSize="small"
                sx={{ color: vlak ? PAARS : '#8a919c', fontSize: 18 }}
              />
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                variant: 'body2',
                fontFamily: FONT_BODY,
                fontWeight: vlak ? 600 : 500,
                fontSize: '0.84rem',
                color: vlak ? PAARS : '#3f4650',
                noWrap: true,
              }}
            />
          </ListItemButton>
        </ListItem>
        {open && <SectieAccordeon sectionKey={item.section} onNavigate={handleClick} />}
      </React.Fragment>
    );
  };

  // Dagelijks werk bovenaan zonder kopje; elke volgende groep krijgt een klein stil kopje
  const renderPrimary = () => items.primary.map((item, i) => {
    const vorige = items.primary[i - 1];
    const nieuweGroep = vorige && vorige.groep !== item.groep;
    const kop = nieuweGroep ? MENU_GROEPEN[item.groep] : null;
    return (
      <React.Fragment key={`g-${item.label}`}>
        {nieuweGroep && (kop ? <GroepKop label={kop} /> : <Box component="li" aria-hidden sx={{ listStyle: 'none', height: 16 }} />)}
        {renderItem(item)}
      </React.Fragment>
    );
  });

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8f9fa',
        borderRight: 'none',
      }}
    >
      {/* Merk (alleen logo + naam) + «/»-knop op focusschermen */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          component={RouterLink}
          to="/vandaag"
          aria-label="Toolframe — naar Vandaag"
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2.5,
            py: 1.75,
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/favicon-192.png`}
            alt=""
            sx={{ width: 30, height: 30, flexShrink: 0, objectFit: 'contain' }}
          />
          <Typography sx={{ fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 800, fontSize: '1.1rem', letterSpacing: '-0.02em', color: '#2d3335', lineHeight: 1 }}>
            Toolframe
          </Typography>
        </Box>
        {onPin && (
          <Tooltip title="Menu vastzetten op dit scherm" placement="right">
            <IconButton size="small" onClick={onPin} aria-label="Menu vastzetten"
              sx={{ ml: 'auto', mr: 1.25, color: '#adb3b5', '&:hover': { color: LEI } }}>
              <KeyboardDoubleArrowRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        {onCollapse && (
          <Tooltip title="Menu inklappen" placement="right">
            <IconButton size="small" onClick={onCollapse} aria-label="Menu inklappen"
              sx={{ ml: 'auto', mr: 1.25, color: '#adb3b5', '&:hover': { color: LEI } }}>
              <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Hoofdmenu — één kolom; scrolt zelf als een uitgeklapte sectie niet past */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <List component="nav" aria-label="Hoofdmenu" sx={{ py: 1 }}>
          {renderPrimary()}
        </List>
      </Box>

      {/* Onderaan: alleen Lab (met recht). Feedback, Admin, Sync-check, de
          BETA/PLANNED-schakelaars en de versie staan in het profielmenu rechtsboven. */}
      {items.bottom.length > 0 && (
        <>
          <Divider sx={{ mx: 2 }} />
          <List sx={{ py: 1 }}>
            {items.bottom.map(renderItem)}
          </List>
        </>
      )}
    </Box>
  );
}

export function VisibilityToggles() {
  const { showBeta, showPlanned, toggleBeta, togglePlanned } = useVisibilityFilters();
  const Pill = ({ active, onToggle, label, accent }) => (
    <Box
      onClick={onToggle}
      role="switch"
      aria-checked={active}
      sx={{
        flex: 1, cursor: 'pointer', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1.25, py: 0.6, borderRadius: '999px',
        border: `1px solid ${active ? accent.color : 'rgba(45,51,53,0.1)'}`,
        bgcolor: active ? accent.bg : 'transparent',
        transition: 'all 0.12s',
        '&:hover': {
          borderColor: active ? accent.color : 'rgba(45,51,53,0.2)',
          bgcolor: active ? accent.bg : 'rgba(0,0,0,0.02)',
        },
      }}
    >
      <Typography sx={{
        fontFamily: '"Inter", system-ui, sans-serif',
        fontSize: '0.6rem', fontWeight: 800,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        color: active ? accent.color : '#adb3b5',
      }}>
        {label}
      </Typography>
      <Box sx={{
        width: 22, height: 12, borderRadius: '999px',
        bgcolor: active ? accent.color : '#d4d6d7',
        position: 'relative', transition: 'background-color 0.12s',
        flexShrink: 0,
      }}>
        <Box sx={{
          position: 'absolute', top: 1, left: active ? 11 : 1,
          width: 10, height: 10, borderRadius: '999px', bgcolor: '#fff',
          transition: 'left 0.12s',
        }} />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ px: 2, py: 1.25 }}>
      <Typography sx={{
        fontFamily: '"Inter", system-ui, sans-serif',
        fontSize: '0.55rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.16em',
        color: '#adb3b5', mb: 1, px: 0.5,
      }}>
        Zichtbaarheid
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Pill
          active={showBeta} onToggle={toggleBeta} label="Beta"
          accent={{ color: '#7c3aed', bg: '#ede9fe' }}
        />
        <Pill
          active={showPlanned} onToggle={togglePlanned} label="Planned"
          accent={{ color: '#d97706', bg: '#fef3c7' }}
        />
      </Box>
    </Box>
  );
}

export default function SidebarNav({ mobileOpen, onMobileClose, hidePersistent, focusKey = null }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [vastgezet, setVastgezet] = useState(leesVastgezet);
  const [zweeft, setZweeft] = useState(false);
  const hoverTimer = useRef(null);

  // Nieuw scherm → zwevend menu dicht
  useEffect(() => { setZweeft(false); }, [focusKey]);
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const zetVast = (open) => {
    if (!focusKey) return;
    setVastgezet((prev) => {
      const next = { ...prev, [focusKey]: open };
      try { window.localStorage.setItem(FOCUS_MENU_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    setZweeft(false);
  };

  // Mobile or full-width pages: only show drawer
  if (isMobile || hidePersistent) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
      >
        <SidebarContent onClose={onMobileClose} />
      </Drawer>
    );
  }

  // Focusscherm: menu ingeklapt tot een strook met »; hover schuift het er tijdelijk
  // overheen (zonder de pagina te verschuiven), » zet het vast (onthouden per scherm).
  if (focusKey && !vastgezet[focusKey]) {
    return (
      <Box
        component="nav"
        aria-label="Hoofdmenu (ingeklapt)"
        onMouseLeave={() => { clearTimeout(hoverTimer.current); setZweeft(false); }}
        sx={{
          width: FOCUS_RAIL_WIDTH,
          flexShrink: 0,
          // Plakt onder de TEST-balk (op productie: 0px → ongewijzigd)
          height: VENSTER_ONDER_TESTBALK,
          position: 'sticky',
          top: TESTBALK_TOP,
          // Zwevend menu boven sticky pagina-elementen (tabelkoppen, balken) houden
          zIndex: zweeft ? theme.zIndex.drawer : theme.zIndex.appBar + 1,
          bgcolor: '#f8f9fa',
          borderRight: '1px solid rgba(45,51,53,0.06)',
        }}
      >
        <Box
          onMouseEnter={() => { hoverTimer.current = setTimeout(() => setZweeft(true), 180); }}
          onMouseLeave={() => clearTimeout(hoverTimer.current)}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1.25 }}
        >
          <Tooltip title="Menu tonen" placement="right">
            <IconButton size="small" onClick={() => zetVast(true)} aria-label="Menu tonen"
              sx={{ color: '#9aa1a6', '&:hover': { color: '#635983', bgcolor: 'rgba(99,89,131,0.08)' } }}>
              <KeyboardDoubleArrowRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {zweeft && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, height: '100%', width: SIDEBAR_WIDTH,
            bgcolor: '#f8f9fa',
            // Duidelijke rand + schaduw: de pagina eronder leest er niet doorheen
            borderRight: '1px solid rgba(45,51,53,0.12)',
            boxShadow: '4px 0 6px rgba(45,51,53,0.05), 14px 0 36px rgba(45,51,53,0.16)',
          }}>
            <SidebarContent onPin={() => zetVast(true)} />
          </Box>
        )}
      </Box>
    );
  }

  // Desktop: persistent sidebar
  return (
    <Box
      component="nav"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        height: VENSTER_ONDER_TESTBALK,
        position: 'sticky',
        top: TESTBALK_TOP,
      }}
    >
      <SidebarContent onCollapse={focusKey ? () => zetVast(false) : undefined} />
    </Box>
  );
}

export { SIDEBAR_WIDTH };
